import React, { useContext, useState, useEffect, ReactNode } from "react";

import { useHistory } from "react-router-dom";
import { UserMetadata, LoginCredentials, UserRole } from "../types";
import { ENDPOINT, Routes } from "../constants";
import { FetchApi } from "../api/fetch";
import { useApi } from "./ApiContext";

type AuthType = {
  currentUser: any;
  currentUserMetadata: UserMetadata | null;
  isLoadingOverlayDisplay: boolean;
  setIsLoadingOverlayDisplay: (setIsLoading: boolean) => void;
  isCurrentUserAdmin: () => boolean;
  isCurrentUserCreator: () => boolean;
  isCurrentUserReviewer: () => boolean;
  login: (loginCredentials: LoginCredentials) => Promise<UserMetadata>;

  logout: () => void;
  getUserJWT: () => Promise<string> | undefined;
};

const AuthContext = React.createContext<AuthType>({
  currentUser: null,
  currentUserMetadata: null,
  isCurrentUserAdmin: () => {
    return false;
  },
  isCurrentUserCreator: () => {
    return false;
  },
  isCurrentUserReviewer: () => {
    return false;
  },

  setIsLoadingOverlayDisplay: () => { },

  isLoadingOverlayDisplay: false,

  login: () => {
    return Promise.resolve({} as UserMetadata);
  },

  logout: () => { },
  getUserJWT: () => {
    return Promise.resolve("");
  },
});

export function useAuth() {
  return useContext(AuthContext);
}

type Props = {
  children: ReactNode;
};

function AuthProvider({ children }: Props) {
  const [currentUser, setCurrentUser] = useState<any>(
    sessionStorage.getItem("userData")
      ? (JSON.parse(sessionStorage.getItem("userData") as string) as any)
      : null
  );
  const [currentUserMetadata, setCurrentUserMetadata] =
    useState<UserMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingOverlayDisplay, setIsLoadingOverlayDisplay] = useState(false);
  const { retrieveCollections } = useApi();
  const history = useHistory();

  async function login(userCredential: any) {
    const userJwt = userCredential.idToken

    let userAttributes = { token: userJwt, ...userCredential?.userData }

    sessionStorage.setItem("userData", JSON.stringify(userAttributes));
    setCurrentUser(userAttributes);

    const userMetadata = await getCurrentUserMetadata(userJwt);
    const username = userCredential?.userData?.["cognito:username"]
    setCurrentUserMetadata(userMetadata);

    sessionStorage.setItem("loggedUser", username)
    setTimeout(() => {
      logout()
    }, 3600000);
    return userMetadata;
  }





  async function logout() {
    setCurrentUser(null);
    setCurrentUserMetadata(null);
    const user = JSON.parse(sessionStorage.getItem("userData") ?? '{}')
    try {
      const response = await fetch(`${ENDPOINT.BASE_AUTH}/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ accessToken: user?.accessToken }),
      })
      await response.json()
      sessionStorage.removeItem("userData")
    } catch (error: any) {
      console.log("Logout Failed");

    }
  }

  function getUserJWT() {
    if (currentUser) {
      const userJWT = JSON.parse(sessionStorage.getItem('userData') || '{}').token
      return userJWT;
    }
  }

  async function getCurrentUserMetadata(userJWT: string) {
    console.log(userJWT);

    setIsLoadingOverlayDisplay(true)
    const response = await FetchApi({
      endpoint: ENDPOINT.GET_ROLE,
      method: "POST",
      JWT: userJWT,
    }) as UserMetadata;
    setIsLoadingOverlayDisplay(false)
    const metadata = { ...response, userId: response?.user_id }
    sessionStorage.setItem("metadata", JSON.stringify(metadata))

    if (history.location.pathname === Routes.LOGIN) {
      if (metadata?.role === UserRole.admin) {
        history.push(Routes.ADMIN_NOTIFICATIONS);
      } else {
        history.push(Routes.MANAGE_COLLECTIONS);
      }
    }
    return metadata
  }

  function isCurrentUserAdmin() {
    return currentUserMetadata?.role === UserRole.admin || false;
  }

  function isCurrentUserCreator() {
    return currentUserMetadata?.role === UserRole.creator || false;
  }

  function isCurrentUserReviewer() {
    return currentUserMetadata?.role === UserRole.reviewer || false;
  }

  useEffect(() => {
    (async () => {
      if (history.location.pathname !== Routes.CREATE_ACCOUNT) {
        setLoading(true);

        const user = JSON.parse(sessionStorage.getItem('userData') || '{}')

        setCurrentUser(user);
        setLoading(false);
      }
    })()
  }, [history]);

  useEffect(() => {
    (async () => {
      const user = JSON.parse(sessionStorage.getItem('userData') || '{}')
      if (user.token) {
        const userMetadata: UserMetadata = await getCurrentUserMetadata(
          user.token
        );
        setCurrentUserMetadata(userMetadata);
        await retrieveCollections(user.token, userMetadata as UserMetadata);
      }
    })();
  }, []);

  const value = {
    currentUser,
    currentUserMetadata,
    isCurrentUserAdmin,
    isCurrentUserCreator,
    isCurrentUserReviewer,
    login,
    logout,
    getUserJWT,
    isLoadingOverlayDisplay,
    setIsLoadingOverlayDisplay,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
